import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Button, 
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  DataTable,
  toast,
  Checkbox,
} from '@etherisc/ui-kit';
import { usePermissions } from '../../hooks/usePermissions';
import {
  CreditCardIcon,
  AlertTriangleIcon,
  DollarSignIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  XIcon,
} from 'lucide-react';
import { PositiveButton, NeutralButton } from '../../components/buttons';
import { apiClient } from '../../services/api';

interface ClaimsPayout {
  id: string;
  claimsAssessmentId: string;
  quoteId: string;
  participantId: string;
  amount: number;
  currency: string;
  state: 'PENDING' | 'PAID' | 'FAILED';
  processedAt: string | null;
  failureReason: string | null;
  createdAt: string;
  claimsAssessment: {
    id: string;
    peril: string;
    runNumber: number;
    reading: number | null;
    eventDate: string | null;
  };
  quote: {
    id: string;
    quoteNumber: string;
    product: { id: string; name: string };
    organization: { id: string; name: string };
  };
  participant: {
    id: string;
    farmer: {
      id: string;
      name: string;
      farmerCode: string;
    } | null;
  };
}

interface Statistics {
  counts: {
    pending: number;
    paid: number;
    failed: number;
    total: number;
  };
  amounts: {
    pending: number;
    paid: number;
    failed: number;
  };
}

export function ClaimsPayments() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  const [payouts, setPayouts] = useState<ClaimsPayout[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // Execute payments modal
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [executing, setExecuting] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [payoutsResult, statsResult] = await Promise.all([
        apiClient.getClaimsPayments({ page, limit, viewAllOrgs: true }),
        apiClient.getClaimsPaymentStatistics(true),
      ]);
      
      setPayouts(payoutsResult.payouts as unknown as ClaimsPayout[]);
      setTotalPages(payoutsResult.pagination.totalPages);
      setTotal(payoutsResult.pagination.total);
      setStatistics(statsResult);
      setError(null);
    } catch (err) {
      console.error('Error fetching payments data:', err);
      setError('Failed to load payments data');
      toast.error('Failed to load payments data');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleSelection = (id: string, state: string) => {
    // Only allow selection of non-paid payments
    if (state === 'PAID') return;
    
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectAllEligible = () => {
    const eligibleIds = payouts
      .filter(p => p.state !== 'PAID')
      .map(p => p.id);
    setSelectedIds(new Set(eligibleIds));
  };

  const handleExecutePayments = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      setExecuting(true);
      const result = await apiClient.executeClaimsPayments(Array.from(selectedIds));
      toast.success(result.message || `Processed ${result.processedCount} payments`);
      setShowExecuteModal(false);
      setSelectedIds(new Set());
      fetchData(); // Refresh data
    } catch (err) {
      console.error('Error executing payments:', err);
      toast.error('Failed to execute payments');
    } finally {
      setExecuting(false);
    }
  };

  const getStateColor = (state: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (state) {
      case 'PENDING': return 'secondary';
      case 'PAID': return 'default';
      case 'FAILED': return 'destructive';
      default: return 'secondary';
    }
  };

  const getStateIcon = (state: string) => {
    switch (state) {
      case 'PENDING': return <ClockIcon size={14} />;
      case 'PAID': return <CheckCircleIcon size={14} />;
      case 'FAILED': return <XCircleIcon size={14} />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="alert alert-error">
          <AlertTriangleIcon size={20} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const eligibleCount = payouts.filter(p => p.state !== 'PAID').length;
  const allEligibleSelected = eligibleCount > 0 && selectedIds.size === eligibleCount;

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const columns: any[] = [
    {
      id: 'select',
      header: () => (
        <Checkbox
          checked={allEligibleSelected}
          onCheckedChange={() => {
            if (selectedIds.size > 0) {
              setSelectedIds(new Set());
            } else {
              selectAllEligible();
            }
          }}
        />
      ),
      cell: ({ row }: any) => {
        const payout = row.original;
        if (payout.state === 'PAID') return null;
        return (
          <Checkbox
            checked={selectedIds.has(payout.id)}
            onCheckedChange={() => toggleSelection(payout.id, payout.state)}
          />
        );
      },
      size: 40,
    },
    {
      accessorKey: 'quote.quoteNumber',
      header: 'Policy',
      cell: ({ row }: any) => (
        <button 
          className="text-primary hover:underline font-medium"
          onClick={() => navigate(`/claims/${row.original.quoteId}`)}
        >
          {row.original.quote.quoteNumber}
        </button>
      ),
    },
    {
      id: 'farmer',
      header: 'Farmer',
      cell: ({ row }: any) => (
        <div>
          <div className="font-medium">{row.original.participant?.farmer?.name || 'Unknown'}</div>
          <div className="text-xs text-base-content/50">{row.original.participant?.farmer?.farmerCode || '-'}</div>
        </div>
      ),
    },
    {
      accessorKey: 'claimsAssessment.peril',
      header: 'Peril',
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ getValue }: any) => {
        const value = getValue();
        return <span className="font-semibold text-success">${Number(value).toLocaleString()}</span>;
      },
    },
    {
      accessorKey: 'state',
      header: 'State',
      cell: ({ getValue }: any) => {
        const state = getValue();
        return (
          <Badge variant={getStateColor(state)} className="flex items-center gap-1 w-fit">
            {getStateIcon(state)}
            {state}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Queued At',
      cell: ({ getValue }: any) => {
        const value = getValue();
        return value ? new Date(value).toLocaleDateString() : '-';
      },
    },
    {
      accessorKey: 'processedAt',
      header: 'Processed At',
      cell: ({ getValue }: any) => {
        const value = getValue();
        return value ? new Date(value).toLocaleDateString() : '-';
      },
    },
    {
      accessorKey: 'quote.organization.name',
      header: 'Organisation',
    },
  ];
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-base-content flex items-center gap-2">
            <CreditCardIcon size={24} />
            Claims Payments
          </h1>
          <p className="text-base-content/70 mt-1">
            Manage and process claim payouts
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-warning/10 rounded-lg">
                  <ClockIcon size={24} className="text-warning" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{statistics.counts.pending}</div>
                  <div className="text-sm text-base-content/70">Pending</div>
                  <div className="text-xs text-base-content/50">${statistics.amounts.pending.toLocaleString()}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-success/10 rounded-lg">
                  <CheckCircleIcon size={24} className="text-success" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{statistics.counts.paid}</div>
                  <div className="text-sm text-base-content/70">Paid</div>
                  <div className="text-xs text-base-content/50">${statistics.amounts.paid.toLocaleString()}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-error/10 rounded-lg">
                  <XCircleIcon size={24} className="text-error" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{statistics.counts.failed}</div>
                  <div className="text-sm text-base-content/70">Failed</div>
                  <div className="text-xs text-base-content/50">${statistics.amounts.failed.toLocaleString()}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <DollarSignIcon size={24} className="text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{statistics.counts.total}</div>
                  <div className="text-sm text-base-content/70">Total Payouts</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCardIcon size={18} />
              Payment Queue
            </CardTitle>
            {selectedIds.size > 0 && hasPermission('claims:approve') && (
              <PositiveButton
                onClick={() => setShowExecuteModal(true)}
                showIcon={false}
              >
                <PlayIcon size={16} />
                Execute Payments ({selectedIds.size})
              </PositiveButton>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {payouts.length === 0 ? (
            <div className="text-center py-8 text-base-content/50">
              <CreditCardIcon size={48} className="mx-auto mb-4 opacity-50" />
              <p>No payments in queue.</p>
              <p className="text-sm">Approved claims are automatically queued for payment.</p>
            </div>
          ) : (
            <>
              <DataTable
                data={payouts}
                columns={columns}
                enableSorting={true}
              />
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-base-content/70">
                    Showing {payouts.length} of {total} payments
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-3 text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Execute Payments Confirmation Modal */}
      {showExecuteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-base-100 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Execute Payments</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowExecuteModal(false)}>
                <XIcon size={20} />
              </Button>
            </div>
            <p className="text-base-content/70">
              Are you sure you want to execute {selectedIds.size} payment(s)?
            </p>
            <p className="text-sm text-base-content/50 mt-2">
              This will mark the selected payments as paid. In production, this would trigger actual payment processing.
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <NeutralButton onClick={() => setShowExecuteModal(false)}>
                Cancel
              </NeutralButton>
              <PositiveButton
                onClick={handleExecutePayments}
                disabled={executing}
                showIcon={false}
              >
                {executing ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <PlayIcon size={16} />
                    Execute
                  </>
                )}
              </PositiveButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
